import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardDocumentCheckIcon, HomeIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { CheckIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useChoreList } from '../hooks/useChoreList';
import ChoreGroup from './ChoreGroup';
import ChoreToolbar from './ChoreToolbar';
import DashboardSummary from './DashboardSummary';
import { Alert, Button, Card, EmptyState, SkeletonList } from './ui';

const ChoreList: React.FC<{ onAddChore: () => void }> = ({ onAddChore }) => {
  const list = useChoreList();
  const navigate = useNavigate();
  // Later starts folded, unless a search or filter is narrowing the list — then a match there
  // shouldn't hide. Once opened or closed by hand, it stays that way.
  const [laterToggled, setLaterToggled] = useState<boolean | null>(null);
  const laterOpen = laterToggled ?? list.hasFilters;

  if (list.loading) return <SkeletonList label="Loading chores…" />;
  if (list.error) return <Alert tone="error" title="Couldn't load your chores">{list.error}</Alert>;

  if (!list.hasHouseholds) {
    return (
      <Card padding="none">
        <EmptyState
          icon={HomeIcon}
          title="Start with a household"
          description="Chores belong to a household: the home, cabin or office you share them in. Create one, then add its chores."
          action={<Button variant="primary" icon={PlusIcon} onClick={() => navigate('/households')}>Create a household</Button>}
        />
      </Card>
    );
  }

  if (list.isEmpty && list.childEverywhere) {
    return (
      <Card padding="none">
        <EmptyState
          icon={ClipboardDocumentCheckIcon}
          title="Nothing for you yet"
          description="When a chore is assigned to you, it shows up here."
        />
      </Card>
    );
  }

  if (list.isEmpty) {
    return (
      <Card padding="none">
        <EmptyState
          icon={ClipboardDocumentCheckIcon}
          title="No chores yet"
          description="Add the things that need doing again and again, and Chores Champ tells you when they're due."
          action={<Button variant="primary" icon={PlusIcon} onClick={onAddChore}>Add your first chore</Button>}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <DashboardSummary counts={list.counts} selected={list.filters.selectedUrgency} onToggle={list.toggleUrgency} />

      <ChoreToolbar
        households={list.households ?? []}
        availableAssignees={list.availableAssignees}
        availableLabels={list.availableLabels}
        availableScheduleKinds={list.availableScheduleKinds}
        view={list.view}
        showWhose={list.showWhose}
        setView={list.setView}
        filters={list.filters}
        selectedAssigneeName={list.selectedAssigneeName}
        selectedScheduleLabel={list.selectedScheduleLabel}
        panelFilterCount={list.panelFilterCount}
        hasFilters={list.hasFilters}
        setHousehold={list.setHousehold}
        setAssignee={list.setAssignee}
        toggleLabel={list.toggleLabel}
        setScheduleKind={list.setScheduleKind}
        setSearchText={list.setSearchText}
        toggleUrgency={list.toggleUrgency}
        clearFilters={list.clearFilters}
      />

      {list.allCaughtUp && (
        <Card className="flex items-start gap-4" padding="lg">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-success text-white dark:bg-emerald-500/15 dark:text-emerald-400">
            <CheckIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">All caught up</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Nothing is overdue or due today.</p>
            {list.nextUp && (
              <p className="mt-3 text-sm text-gray-900 dark:text-gray-100">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Next up</span>
                <span className="mt-0.5 block">
                  <span className="font-medium">{list.nextUp.name}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {' · '}{list.nextUp.dueText}{list.nextUp.householdName && ` · ${list.nextUp.householdName}`}
                  </span>
                </span>
              </p>
            )}
          </div>
        </Card>
      )}

      {list.nothingForYou ? (
        <Card padding="none">
          <EmptyState
            icon={UserIcon}
            title="Nothing for you"
            description="Every chore is assigned to someone else."
            action={<Button onClick={() => list.setView('everyone')}>Show everyone's chores</Button>}
          />
        </Card>
      ) : list.noResults ? (
        <Card padding="none">
          <EmptyState
            icon={MagnifyingGlassIcon}
            title={list.filters.selectedUrgency ? 'Nothing here right now' : 'No chores match these filters'}
            action={<Button onClick={list.clearFilters}>{list.view === 'mine' || list.childEverywhere ? 'Clear filters' : 'Show every chore'}</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {list.groups.map(group => (
            <ChoreGroup
              key={group.urgency}
              urgency={group.urgency}
              label={group.label}
              chores={group.chores}
              {...(group.urgency === 'later' && !list.filters.selectedUrgency && {
                collapsible: true,
                open: laterOpen,
                onToggle: () => setLaterToggled(!laterOpen),
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ChoreList;
